import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getETFPriceChart } from '@/src/services/etfDetails';
import { selectEtfDetails } from '@/src/store/reducers/etf/etfDetailsSlice';
import { ETFChartData, ChartResponse } from '../types';
import { ChartDetailsShimmer } from '../../../components/Loaders/ChartDetailsShimmer';
import { AreaChartComponent } from '../../../components/Charts/AreaChartComponent';
import { COLOR_CODES } from '@/src/common/enum';

interface ETFPriceChartProps {
  periodLevel: string;
}

export const ETFPriceChart: React.FC<ETFPriceChartProps> = ({
  periodLevel,
}) => {
  const {
    data: { cocode, exchange },
  } = useSelector(selectEtfDetails);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<ETFChartData[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cocode && exchange && periodLevel) {
      setLoading(true);
      setError(null);
      getETFPriceChart(cocode, exchange.toString(), periodLevel)
        .then((response) => {
          const dataWithPercentage = response?.data?.data?.map(
            (item: ChartResponse, index: number, array: ChartResponse[]) => {
              const prevPrice = index > 0 ? array[index - 1].Price : null;
              const percentage = prevPrice
                ? ((item.Price - prevPrice) / prevPrice) * 100
                : 0;
              return {
                ...item,
                PercentageChange: parseFloat(percentage.toFixed(2)),
              };
            },
          );
          setChartData(dataWithPercentage);
        })
        .catch(() => {
          setError('Failed to load chart data');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [cocode, exchange, periodLevel]);

  if (isLoading) {
    return <ChartDetailsShimmer />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const firstClose = chartData[0]?.Price ?? 0;
  const lastClose = chartData[chartData.length - 1]?.Price ?? 0;
  const areaColor =
    lastClose > firstClose ? COLOR_CODES.SKY_BLUE : COLOR_CODES.ORANGE;

  return (
    <AreaChartComponent
      data={chartData}
      areaColor={areaColor}
      xDataKey="TradeDate"
      yDataKey="Price"
    />
  );
};
